import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronDown,
  ThumbsUp,
  ThumbsDown,
  Info,
  Zap,
  Shield,
  BarChart,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CompetitorProps {
  competitors?: {
    name: string;
    company: string;
    type: string;
    keyFeatures: string[];
    advantages: string[];
    disadvantages: string[];
    valueProposition: string;
  }[];
  prudentialAdvantages?: string[];
  className?: string;
}

export function Competitors({
  competitors = [],
  prudentialAdvantages = [],
  className,
}: CompetitorProps) {
  const { t } = useTranslation();
  const [expandedCompetitor, setExpandedCompetitor] = useState<string | null>(
    null,
  );

  // If no competitive data available, don't render the component
  if (!competitors.length || !prudentialAdvantages.length) {
    return null;
  }

  const toggleCompetitor = (name: string) => {
    setExpandedCompetitor(expandedCompetitor === name ? null : name);
  };

  return (
    <div className={className}>
      <h3 className="text-base font-semibold mb-4 text-slate-800 flex items-center">
        <BarChart className="w-5 h-5 mr-2 text-primary" />
        {t('custom.roleplay.headers.competitiveIntelligence')}
      </h3>

      {/* Prudential Advantages */}
      <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100 shadow-sm">
        <h4 className="text-sm font-medium text-emerald-800 mb-3 flex items-center">
          <Shield className="w-4 h-4 mr-2 text-emerald-600" />
          {t('custom.roleplay.competitors.yourProductAdvantages')}
        </h4>
        <ul className="space-y-2 pl-1">
          {prudentialAdvantages.map((advantage, index) => (
            <li key={index} className="flex items-start">
              <span className="text-emerald-500 mr-2 font-bold">•</span>
              <span className="text-sm text-slate-700">{advantage}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Competitor List */}
      <div className="space-y-3">
        {competitors.map((competitor, idx) => (
          <div
            key={`${competitor.company}-${competitor.name}-${idx}`}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
          >
            <button
              onClick={() => toggleCompetitor(competitor.name)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium text-slate-800">
                  {competitor.name}
                </span>
                <span className="text-xs text-slate-500">
                  {competitor.company}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-xs font-medium text-slate-500 mr-2">
                  {expandedCompetitor === competitor.name ? t('custom.roleplay.buttons.hide') : t('custom.roleplay.buttons.show')}{' '}
                  {t('custom.roleplay.competitors.details')}
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-slate-400 transform transition-transform ${
                    expandedCompetitor === competitor.name ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </button>

            {expandedCompetitor === competitor.name && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-slate-200 px-4 py-3"
              >
                <div className="text-xs text-slate-500 mb-3 inline-block px-2 py-0.5 bg-slate-100 rounded-full">
                  <span className="font-medium">{t('custom.roleplay.competitors.type')}</span> {competitor.type}
                </div>

                <div className="mb-4">
                  <h5 className="text-xs font-medium text-slate-700 mb-2 uppercase tracking-wide">
                    {t('custom.roleplay.competitors.keyFeatures')}
                  </h5>
                  <ul className="text-xs text-slate-600 space-y-1.5 ml-4">
                    {competitor.keyFeatures.map((feature, idx) => (
                      <li key={idx} className="list-disc">
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg">
                    <h5 className="text-xs font-medium text-green-800 mb-2 flex items-center">
                      <ThumbsUp className="w-3 h-3 mr-1" />
                      {t('custom.roleplay.competitors.theirStrengths')}
                    </h5>
                    <ul className="text-xs text-slate-700 space-y-1.5 ml-3">
                      {competitor.advantages.map((adv, idx) => (
                        <li key={idx} className="list-disc">
                          {adv}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-red-100 p-3 rounded-lg">
                    <h5 className="text-xs font-medium text-red-800 mb-2 flex items-center">
                      <ThumbsDown className="w-3 h-3 mr-1" />
                      {t('custom.roleplay.competitors.theirLimitations')}
                    </h5>
                    <ul className="text-xs text-slate-700 space-y-1.5 ml-3">
                      {competitor.disadvantages.map((disadv, idx) => (
                        <li key={idx} className="list-disc">
                          {disadv}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 rounded-lg">
                  <h5 className="text-xs font-medium text-indigo-800 mb-2 flex items-center">
                    <Zap className="w-3 h-3 mr-1" />
                    {t('custom.roleplay.competitors.positioningStrategy')}
                  </h5>
                  <p className="text-xs text-slate-700">
                    {competitor.valueProposition}
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
